// pages/Register.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Navigation from "../../components/Common/header/Navigation";
import Footer from "../../components/Common/footer/Footer";
import "../../styles/Auth.css";

const Register = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // Destructure googleSignIn here
    const { signup, googleSignIn } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        // 1. Email Format Validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return setError("Please enter a valid email address.");
        }

        // 2. Password Strength Validation (Min 8 chars, 1 letter, 1 number)
        if (password.length < 8) {
            return setError("Password must be at least 8 characters long.");
        }
        if (!/(?=.*[A-Za-z])(?=.*\d)/.test(password)) {
            return setError("Password must contain at least one letter and one number.");
        }

        // 3. Password Match Validation
        if (password !== confirmPassword) {
            return setError("Passwords do not match.");
        }

        try {
            setLoading(true);
            await signup(email, password, displayName);
            navigate("/");
        } catch (error) {
            // Map Firebase registration errors
            if (error.code === 'auth/email-already-in-use') {
                setError("An account with this email already exists.");
            } else if (error.code === 'auth/invalid-email') {
                setError("The email address provided is invalid.");
            } else {
                setError("Failed to create an account. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        try {
            setError("");
            setLoading(true);
            await googleSignIn();
            navigate("/");
        } catch (error) {
            if (error.code !== 'auth/popup-closed-by-user') {
                setError("Google sign-in failed. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <Navigation />
            <div className="container py-5">
                <div className="auth-card">
                    <h2 className="auth-title">Sign Up</h2>
                    <div className="auth-divider" />
                    {error && <div className="auth-error">{error}</div>}

                    <button
                        disabled={loading}
                        className="auth-btn-google"
                        onClick={handleGoogleSignIn}
                        type="button"
                    >
                        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        Sign up with Google
                    </button>

                    <div className="auth-or-divider">
                        <span>OR</span>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="auth-field">
                            <label htmlFor="displayName" className="auth-label">Full Name</label>
                            <input
                                type="text"
                                className="auth-input"
                                id="displayName"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="auth-field">
                            <label htmlFor="email" className="auth-label">Email</label>
                            <input
                                type="email"
                                className="auth-input"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="auth-field">
                            <label htmlFor="password" className="auth-label">Password</label>
                            <input
                                type="password"
                                className="auth-input"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={8}
                            />
                            <p className="auth-hint">Must be at least 8 characters with 1 letter and 1 number</p> {/* <-- Update the hint */}
                        </div>
                        <div className="auth-field">
                            <label htmlFor="confirmPassword" className="auth-label">Confirm Password</label>
                            <input
                                type="password"
                                className="auth-input"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                        </div>
                        <button
                            disabled={loading}
                            className="auth-btn"
                            type="submit"
                        >
                            {loading ? "Creating Account..." : "Create Account"}
                        </button>
                    </form>
                    <p className="auth-footer-text">
                        Already have an account? <Link to="/login">Log In</Link>
                    </p>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Register;